/**
 * OptionPickerPopover — a lightweight Button + Popover picker for small option lists.
 *
 * Renders a subtle Button (matching the field picker style) that opens a popover
 * with a simple list of options. Selecting an option closes the popover.
 *
 * Used for: aggregation, sort, limit, direction — any small fixed-option list.
 */
import { useCallback, useState } from "react";
import {
  Button,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ChevronDownRegular } from "@fluentui/react-icons";
import { useFieldMappingStyles } from "./styles";

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  surface: {
    padding: "4px 0",
    minWidth: "120px",
    maxHeight: "300px",
    overflowY: "auto",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    borderRadius: 0,
    border: "none",
    backgroundColor: "transparent",
    width: "100%",
    textAlign: "left" as const,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  optionSelected: {
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PickerOption {
  value: string;
  label: string;
}

export interface OptionPickerPopoverProps {
  options: readonly PickerOption[];
  value: string | undefined;
  onSelect: (value: string) => void;
  /** CSS class applied to the trigger Button */
  className?: string;
  /** Placeholder when no value is selected */
  placeholder?: string;
  /** Optional prefix shown before the selected label (e.g. "Sort: ") */
  prefix?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function OptionPickerPopover({
  options,
  value,
  onSelect,
  className,
  placeholder = "Select…",
  prefix,
}: OptionPickerPopoverProps) {
  const styles = useStyles();
  const fieldStyles = useFieldMappingStyles();
  const [open, setOpen] = useState(false);

  const selectedLabel =
    options.find((o) => o.value === value)?.label ?? placeholder;

  const displayText = prefix ? `${prefix}${selectedLabel}` : selectedLabel;

  const handleSelect = useCallback(
    (optValue: string) => {
      onSelect(optValue);
      setOpen(false);
    },
    [onSelect],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(_, data) => setOpen(data.open)}
      positioning="below-start"
      trapFocus
    >
      <PopoverTrigger disableButtonEnhancement>
        <Button
          className={mergeClasses(fieldStyles.dropdown, className)}
          size="small"
          appearance="subtle"
          iconPosition="after"
          icon={<ChevronDownRegular />}
        >
          {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`${styles.option}${opt.value === value ? ` ${styles.optionSelected}` : ""}`}
            onClick={() => handleSelect(opt.value)}
            type="button"
          >
            {opt.label}
          </button>
        ))}
      </PopoverSurface>
    </Popover>
  );
}